/**
 * CR310: Default Label Fallback Map
 * CR450: Canonical component-scoped keys + legacy alias bridge
 * 
 * Provides deterministic default titles for value boxes and other
 * labeled elements across all deal types.
 * 
 * Canonical keys are component-scoped (e.g., value_box_title) to
 * eliminate ambiguity between components and labels.
 * 
 * Precedence (highest → lowest):
 * 1. draftOverrides.labels
 * 2. overrides_json.labels
 * 3. Legacy bridge: old keys → canonical keys
 * 4. DEFAULT_LABELS_BY_DEAL_TYPE[dealType]
 */

// ================================================================
// TYPES
// ================================================================

export type DealTypeKey = "BONUS" | "POINTS" | "DISCOUNT" | "IN_KIND" | "UNCLASSIFIED";

/** CR450: Canonical component-scoped label keys */
export type CanonicalLabelKey =
  | "value_box_title"
  | "reward_box_title"
  | "qualifying_spend_box_title"
  | "eligibility_box_title";

/** Legacy label keys (kept for backward compatibility) */
export type LegacyLabelKey =
  | "value_title"
  | "reward_title"
  | "qualifying_spend_title"
  | "eligibility_title"
  | "effective_value_title";

/** All label keys the system recognizes */
export type LabelKey = CanonicalLabelKey | LegacyLabelKey;

// CR450: Alias map — legacy key → canonical key
export const LEGACY_TO_CANONICAL: Record<string, CanonicalLabelKey> = {
  value_title: "value_box_title",
  reward_title: "reward_box_title",
  qualifying_spend_title: "qualifying_spend_box_title",
  eligibility_title: "eligibility_box_title",
  effective_value_title: "value_box_title",
};

// CR450: Reverse map — canonical → legacy (for dual-write)
export const CANONICAL_TO_LEGACY: Record<CanonicalLabelKey, string> = {
  value_box_title: "value_title",
  reward_box_title: "reward_title",
  qualifying_spend_box_title: "qualifying_spend_title",
  eligibility_box_title: "eligibility_title",
};

// ================================================================
// DEFAULT LABELS BY DEAL TYPE (canonical keys)
// ================================================================

export const DEFAULT_LABELS_BY_DEAL_TYPE: Record<DealTypeKey, Record<CanonicalLabelKey, string>> = {
  BONUS: {
    value_box_title: "Effective Value",
    reward_box_title: "Bonus Value",
    qualifying_spend_box_title: "Qualifying Spend",
    eligibility_box_title: "Eligibility",
  },
  POINTS: {
    value_box_title: "Cash Equivalent",
    reward_box_title: "Points Earned",
    qualifying_spend_box_title: "Qualifying Spend",
    eligibility_box_title: "Eligibility",
  },
  DISCOUNT: {
    value_box_title: "Effective Value",
    reward_box_title: "Discount",
    qualifying_spend_box_title: "Qualifying Spend",
    eligibility_box_title: "Eligibility",
  },
  IN_KIND: {
    value_box_title: "Estimated Value",
    reward_box_title: "Free Item / Perk",
    qualifying_spend_box_title: "Qualifying Spend",
    eligibility_box_title: "Eligibility",
  },
  UNCLASSIFIED: {
    value_box_title: "Value",
    reward_box_title: "Reward",
    qualifying_spend_box_title: "Qualifying Spend",
    eligibility_box_title: "Eligibility",
  },
};

// ================================================================
// CANONICAL LABEL KEYS
// ================================================================

export const CANONICAL_LABEL_KEYS: CanonicalLabelKey[] = [
  "value_box_title",
  "reward_box_title",
  "qualifying_spend_box_title",
  "eligibility_box_title",
];

/** Legacy keys still recognized in resolution */
export const LEGACY_LABEL_KEYS: string[] = [
  "value_title",
  "reward_title",
  "qualifying_spend_title",
  "eligibility_title",
  "effective_value_title",
];

// ================================================================
// HELPER FUNCTIONS
// ================================================================

/**
 * Get default labels for a specific deal type
 */
export function getDefaultLabelsForDealType(dealType: string): Record<CanonicalLabelKey, string> {
  const normalizedType = dealType.toUpperCase() as DealTypeKey;
  return DEFAULT_LABELS_BY_DEAL_TYPE[normalizedType] || DEFAULT_LABELS_BY_DEAL_TYPE.UNCLASSIFIED;
}

/**
 * Get a specific default label for a deal type
 */
export function getDefaultLabel(dealType: string, labelKey: CanonicalLabelKey): string {
  const defaults = getDefaultLabelsForDealType(dealType);
  return defaults[labelKey] || labelKey;
}

/**
 * CR450: Resolve a label value from a labels record, checking canonical key first,
 * then legacy aliases. Returns undefined if not found.
 */
export function resolveLabelFromRecord(
  labels: Record<string, string | undefined> | null | undefined,
  canonicalKey: CanonicalLabelKey
): string | undefined {
  if (!labels) return undefined;
  
  // Check canonical key first
  const canonical = labels[canonicalKey];
  if (canonical && canonical.trim() !== "") return canonical;
  
  // Check legacy alias
  const legacyKey = CANONICAL_TO_LEGACY[canonicalKey];
  if (legacyKey) {
    const legacy = labels[legacyKey];
    if (legacy && legacy.trim() !== "") return legacy;
  }
  
  // Special case: effective_value_title → value_box_title
  if (canonicalKey === "value_box_title") {
    const effective = labels["effective_value_title"];
    if (effective && effective.trim() !== "") return effective;
  }
  
  return undefined;
}

/**
 * Check if a deal type key is valid
 */
export function isValidDealType(dealType: string): dealType is DealTypeKey {
  return ["BONUS", "POINTS", "DISCOUNT", "IN_KIND", "UNCLASSIFIED"].includes(dealType.toUpperCase());
}

/**
 * Normalize a deal type key to uppercase
 */
export function normalizeDealType(dealType: string): DealTypeKey {
  const upper = dealType.toUpperCase();
  if (isValidDealType(upper)) return upper;
  return "UNCLASSIFIED";
}
